import { Link } from 'react-router-dom';

const EFFECTIVE_DATE = 'June 1, 2025';
const VERSION = '1.0';
const CONTACT_EMAIL = 'info@themoneymap.online';
const CONTACT_ADDRESS = 'Windsor, ON, Canada';

export default function PrivacyPage() {
  return (
    <div className="min-h-screen bg-background text-foreground">
      <div className="max-w-3xl mx-auto px-6 py-12 space-y-8">

        {/* Header */}
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <Link to="/" className="text-sm text-blue-500 hover:underline">TheMoneyMap</Link>
            <span className="text-muted-foreground text-sm">›</span>
            <span className="text-sm text-muted-foreground">Privacy Policy</span>
          </div>
          <h1 className="text-3xl font-bold">Privacy Policy</h1>
          <p className="text-sm text-muted-foreground">Version {VERSION} — Effective {EFFECTIVE_DATE}</p>
        </div>

        {/* Plain-language summary box */}
        <div className="rounded-xl border bg-blue-50 dark:bg-blue-950/30 border-blue-200 dark:border-blue-800 p-5 space-y-2">
          <p className="text-sm font-semibold text-blue-800 dark:text-blue-300">Plain-language summary</p>
          <ul className="text-sm text-blue-900 dark:text-blue-200 space-y-1 list-disc ml-4">
            <li>We collect your email and the financial data you enter — nothing more.</li>
            <li>We never sell your data or share it with advertisers.</li>
            <li>Payments are handled by Stripe. AI categorisation uses OpenAI (you can turn it off).</li>
            <li>You can request, correct, export, or delete your data at any time by emailing us.</li>
            <li>We use only essential session cookies — no advertising or analytics trackers.</li>
          </ul>
        </div>

        <div className="space-y-6 text-sm leading-relaxed">

          <section>
            <h2 className="text-lg font-semibold mb-2">1. Who We Are</h2>
            <p>
              TheMoneyMap is operated by an individual in Windsor, Ontario, Canada. Contact us at{' '}
              <a href={`mailto:${CONTACT_EMAIL}`} className="text-blue-500 hover:underline">{CONTACT_EMAIL}</a>{' '}
              for any privacy question or request.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold mb-2">2. What We Collect and Why</h2>
            <table className="w-full text-sm border-collapse">
              <thead>
                <tr className="bg-muted/50">
                  <th className="text-left p-2 border border-border font-medium">Data</th>
                  <th className="text-left p-2 border border-border font-medium">Why</th>
                </tr>
              </thead>
              <tbody>
                {[
                  ['Name and email address', 'Create and secure your account; send transactional emails'],
                  ['Password (hashed with bcrypt)', 'Authenticate you'],
                  ['Transactions, income, assets, and liabilities you enter', 'Provide the core service — tracking and analysis'],
                  ['Country and currency preference', 'Display amounts in your preferred currency'],
                  ['Session token (HttpOnly cookie)', 'Keep you logged in securely'],
                  ['Approximate login timestamps and IP', 'Detect brute-force attacks and suspicious access'],
                  ['Subscription status', 'Determine your access tier; process payments via Stripe'],
                  ['Age confirmation and ToS acceptance timestamp', 'Comply with our eligibility and consent requirements'],
                ].map(([data, reason]) => (
                  <tr key={data} className="border-b border-border">
                    <td className="p-2 border border-border align-top font-medium">{data}</td>
                    <td className="p-2 border border-border align-top text-muted-foreground">{reason}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <p className="mt-3 text-muted-foreground">
              We do not collect a full date of birth. Age is verified by a declaration checkbox at signup only.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold mb-2">3. Third Parties We Use</h2>
            <p className="mb-2">We share limited data with these service providers — no one else:</p>
            <div className="space-y-3">
              {[
                {
                  name: 'Stripe, Inc. (USA)',
                  what: 'Payment processing. Stripe receives your name, email, and payment card details when you subscribe. We never see or store your card number.',
                  link: 'https://stripe.com/privacy',
                },
                {
                  name: 'OpenAI, L.L.C. (USA)',
                  what: 'AI-powered transaction categorisation (Pro accounts only, can be disabled in Settings). We send anonymised transaction descriptions — never your name, email, or account balance.',
                  link: 'https://openai.com/policies/privacy-policy',
                },
                {
                  name: 'Resend, Inc. (USA)',
                  what: 'Transactional email delivery (verification, password reset, welcome). Resend receives your email address and the email content.',
                  link: 'https://resend.com/legal/privacy-policy',
                },
              ].map(({ name, what, link }) => (
                <div key={name} className="rounded-lg border p-3 space-y-1">
                  <p className="font-medium">{name}</p>
                  <p className="text-muted-foreground">{what}</p>
                  <a href={link} target="_blank" rel="noreferrer" className="text-blue-500 hover:underline text-xs">
                    View their privacy policy →
                  </a>
                </div>
              ))}
            </div>
          </section>

          <section>
            <h2 className="text-lg font-semibold mb-2">4. Cross-Border Data Transfers</h2>
            <p>
              Stripe, OpenAI, and Resend are based in the United States. Data shared with them is
              processed in the US, which may have different privacy protections than Canada. These
              providers maintain industry-standard security programs and comply with applicable
              data-transfer frameworks.
            </p>
            <p className="mt-2">
              <strong>Québec residents:</strong> By using TheMoneyMap you authorise the transfer of
              your personal information to these third-party processors outside Québec, as permitted
              under the Act respecting the protection of personal information in the private sector
              (Law 25). A Privacy Impact Assessment for these transfers is available on request.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold mb-2">5. Cookies and Tracking</h2>
            <p>
              We use a single <strong>HttpOnly session cookie</strong> to keep you logged in. No
              advertising cookies, no third-party analytics trackers, and no fingerprinting scripts
              are deployed. You cannot meaningfully use the app without the session cookie, so we do
              not show a cookie-consent banner.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold mb-2">6. Data Retention</h2>
            <p>
              We keep your data for as long as your account is active. When you delete your account
              (see below), all of your personal and financial records are permanently erased from our
              database within 30 days. We do not retain anonymised or aggregated copies of your
              individual financial data after deletion.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold mb-2">7. Your Rights</h2>
            <p className="mb-2">
              Under the <em>Personal Information Protection and Electronic Documents Act</em> (PIPEDA),
              Québec Law 25, and other applicable Canadian privacy laws, you have the right to:
            </p>
            <ul className="list-disc ml-6 space-y-1 text-muted-foreground">
              <li><strong className="text-foreground">Access</strong> — request a copy of the personal information we hold about you.</li>
              <li><strong className="text-foreground">Correction</strong> — ask us to correct inaccurate personal information.</li>
              <li><strong className="text-foreground">Deletion</strong> — delete your account and all associated data from Settings → Account → Delete Account, or email us.</li>
              <li><strong className="text-foreground">Portability</strong> — request a machine-readable export of your financial data.</li>
              <li><strong className="text-foreground">Withdraw consent</strong> — you may withdraw consent to AI feature processing at any time in Settings (this does not affect data already processed).</li>
              <li><strong className="text-foreground">Object to automated decision-making</strong> — AI categorisation is a convenience feature; you can correct any category at any time without consequence.</li>
            </ul>
            <p className="mt-3">
              To exercise any right, use the <strong>Settings → Request My Data</strong> button or email{' '}
              <a href={`mailto:${CONTACT_EMAIL}`} className="text-blue-500 hover:underline">{CONTACT_EMAIL}</a>.
              We will respond within <strong>30 days</strong>.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold mb-2">8. Security</h2>
            <p>
              All data is transmitted over HTTPS. Passwords are stored as bcrypt hashes — we cannot
              read your password. Session tokens are HttpOnly and cannot be accessed by JavaScript.
              We log failed login attempts to detect brute-force attacks.
            </p>
            <p className="mt-2 text-muted-foreground text-xs">
              Note: no internet-connected system can be guaranteed 100% secure. We apply reasonable
              technical safeguards but cannot make absolute security guarantees.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold mb-2">9. Children</h2>
            <p>TheMoneyMap is not directed at anyone under 19. We do not knowingly collect data from minors.</p>
          </section>

          <section>
            <h2 className="text-lg font-semibold mb-2">10. Changes to This Policy</h2>
            <p>
              Material changes will be communicated by email at least 30 days before the new version
              takes effect. The effective date at the top of this page always reflects the current version.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold mb-2">11. Contact</h2>
            <p>
              Privacy enquiries and rights requests:<br />
              <a href={`mailto:${CONTACT_EMAIL}`} className="text-blue-500 hover:underline">{CONTACT_EMAIL}</a><br />
              {CONTACT_ADDRESS}
            </p>
          </section>
        </div>

        <div className="pt-4 border-t flex gap-4 text-sm">
          <Link to="/terms" className="text-blue-500 hover:underline">Terms of Service</Link>
          <Link to="/login" className="text-muted-foreground hover:underline">Back to login</Link>
        </div>
      </div>
    </div>
  );
}
